import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay } from 'rxjs';

export type MeteoData = {
  temperature: number;
  icone: string;
  label: string;
  couleur: string;
};

type OpenMeteoResponse = {
  current: {
    temperature_2m: number;
    weather_code: number;
  };
};

/** Correspondances codes WMO → icône Material Symbols + label français + couleur. */
function interpreterCode(code: number): { icone: string; label: string; couleur: string } {
  if (code === 0)         return { icone: 'wb_sunny',          label: 'Ensoleillé',     couleur: 'var(--amber)' };
  if (code <= 2)          return { icone: 'partly_cloudy_day', label: 'Peu nuageux',    couleur: 'var(--amber)' };
  if (code === 3)         return { icone: 'cloud',             label: 'Couvert',        couleur: 'var(--muted)' };
  if (code <= 48)         return { icone: 'foggy',             label: 'Brouillard',     couleur: 'var(--muted)' };
  if (code <= 55)         return { icone: 'rainy',             label: 'Bruine',         couleur: 'var(--blue)'  };
  if (code <= 67)         return { icone: 'rainy',             label: 'Pluie',          couleur: 'var(--blue)'  };
  if (code <= 77)         return { icone: 'weather_snowy',     label: 'Neige',          couleur: 'var(--cyan)'  };
  if (code <= 82)         return { icone: 'rainy',             label: 'Averses',        couleur: 'var(--blue)'  };
  if (code <= 86)         return { icone: 'weather_snowy',     label: 'Averses neige',  couleur: 'var(--cyan)'  };
  return                         { icone: 'thunderstorm',      label: 'Orage',          couleur: 'var(--violet)'};
}

/**
 * Service météo basé sur Open-Meteo (https://open-meteo.com/).
 * Gratuit, sans clé API, CORS natif, 10 000 appels/jour.
 * Les résultats sont mis en cache par coordonnées pour éviter les appels redondants.
 */
@Injectable({ providedIn: 'root' })
export class MeteoService {
  private readonly http = inject(HttpClient);

  /** Cache Observable par clé "lat,lon" (arrondi à 2 décimales ~1 km). */
  private readonly cache = new Map<string, Observable<MeteoData>>();

  /**
   * Retourne les données météo actuelles pour les coordonnées données.
   * L'Observable est partagé et rejoué pour les abonnés ultérieurs (shareReplay).
   */
  getMeteo(lat: number, lon: number): Observable<MeteoData> {
    const cle = `${lat.toFixed(2)},${lon.toFixed(2)}`;

    if (!this.cache.has(cle)) {
      const url =
        `https://api.open-meteo.com/v1/forecast` +
        `?latitude=${lat}&longitude=${lon}` +
        `&current=temperature_2m,weather_code` +
        `&timezone=Europe%2FParis`;

      const obs = this.http.get<OpenMeteoResponse>(url).pipe(
        map((res) => {
          const { icone, label, couleur } = interpreterCode(res.current.weather_code);
          return {
            temperature: Math.round(res.current.temperature_2m),
            icone,
            label,
            couleur,
          };
        }),
        shareReplay(1),
      );

      this.cache.set(cle, obs);
    }

    return this.cache.get(cle)!;
  }
}
